<template>
  <div class="cms-form-header">
    <div class="cms-form-breadcrumb">
      <button class="cms-btn-back" @click="$emit('back')">
        <v-icon icon="arrow-left" size="sm" />
        {{ backText }}
      </button>
      <h1 class="cms-form-title">{{ title }}</h1>
    </div>
    <div v-if="showDraft || showPublish || $slots['extra-actions']" class="cms-form-actions">
      <slot name="extra-actions" />
      <cms-button
        v-if="showDraft"
        variant="secondary"
        :disabled="disabled"
        @click="$emit('save-draft')"
      >
        {{ draftText }}
      </cms-button>
      <cms-button
        v-if="showPublish"
        variant="primary"
        :disabled="disabled"
        @click="$emit('save-publish')"
      >
        {{ publishText }}
      </cms-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import CmsButton from './CmsButton.vue';

interface Props {
  title: string;
  backText?: string;
  draftText?: string;
  publishText?: string;
  showDraft?: boolean;
  showPublish?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showDraft: true,
  showPublish: true,
  disabled: false
});

const { t } = useI18n();

// Default texts from i18n
const backText = props.backText || t('general.back');
const draftText = props.draftText || t('general.saveDraft');
const publishText = props.publishText || t('general.saveAndPublish');

defineEmits<{
  (e: 'back'): void;
  (e: 'save-draft'): void;
  (e: 'save-publish'): void;
}>();
</script>

<style lang="scss" scoped>
@import './cms-styles.scss';

.cms-form-header {
  position: sticky;
  margin-top: 20px;
  top: 100px;
  z-index: 100;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0.5rem;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
  background-color: white;
  border-radius: 10px;
  border: 1px solid $cms-border-color;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.cms-form-breadcrumb {
  display: flex;
  align-items: center;
  gap: $cms-spacing-md;
}

.cms-btn-back {
  display: flex;
  opacity: 0.6;
  align-items: center;
  gap: $cms-spacing-sm;
  border: none;
  background: transparent;
  color: black;
  text-decoration: none;
  font-weight: 500;
  transition: all $cms-transition-base;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateX(-2px);
  }
}

.cms-form-title {
  font-size: $cms-font-size-xl;
  font-weight: 600;
  margin: 0;
}

.cms-form-actions {
  display: flex;
  gap: $cms-spacing-md;
}

@include cms-responsive-mobile {
  .cms-form-header {
    flex-direction: column;
    gap: $cms-spacing-md;
    align-items: stretch;
    padding: $cms-spacing-md;
  }

  .cms-form-actions {
    flex-direction: column;
  }
}
</style>



